import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { format } from 'date-fns';
import styled from 'styled-components';

import { Collapse } from '@components/animators/collapse';
import { Breadcrumb } from '@components/breadcrumb';
import { Button } from '@components/button';
import { Card } from '@components/card';
import { createForm, useCreateFormStore } from '@components/form';
import { FormLabel } from '@components/form-label';
import { Header } from '@components/header';
import { IconAddPlus } from '@components/icons/add-plus';
import { IconFlagGB } from '@components/icons/flag-gb';
import { IconFlagIS } from '@components/icons/flag-is';
import { RelativeSpinner } from '@components/loading-spinner';
import { PageContent } from '@components/page-containers/page-container';
import {
  ColumnLeftSidebar,
  ColumnMiddleWithLeftSidebar,
  Grid,
} from '@components/page-containers/page-containers';
import { SelectOption } from '@components/select';
import { Separator } from '@components/separator';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';
import { isEditorHtmlEmpty } from '@utils/draftjs';

import { useCreateJob, useGetJob, useUpdateJob } from 'api/jobs/hooks';
import { useJobLabels } from 'api/labels/hooks';
import { useWorkTypes } from 'api/worktypes/hooks';
import { WorkType } from 'api/worktypes/requests';
import { FLAG_MAPPER_SMALL } from 'components/iconMappers';
import { useModal } from 'features/modals';
import { usePermissions } from 'features/permissions';
import { getHtmlContentLength, shortDescriptionMaxLength } from 'models/jobs';
import { useStore } from 'models/Provider';
import { IDepartment, IJobLabel, ILocation, ITranslation } from 'models/types';
import { JobDescriptionEditor } from 'pages/CreateJob/Description/JobDescriptionEditor';
import { ShortDescriptionHtml } from 'pages/CreateJob/Description/short-description-html';
import { CreateJobType, Job, JobLanguage } from 'types/jobs';

import { useJobLanguages } from './language-box';
import { Actions } from './top-bar-actions';
import { useUnsavedChangesPrompt } from './unsaved-changes-modal';

type JobPostingJobType = {
  isTitle?: string;
  enTitle?: string;
  plTitle?: string;
  noTitle?: string;
  svTitle?: string;
  frTitle?: string;
  enShortDescription?: string;
  isShortDescription?: string;
  plShortDescription?: string;
  noShortDescription?: string;
  svShortDescription?: string;
  frShortDescription?: string;
  isDescription?: string;
  enDescription?: string;
  plDescription?: string;
  noDescription?: string;
  svDescription?: string;
  frDescription?: string;
  deadlineSwitch?: boolean;
  schedulePublishSwitch?: boolean;
  labels?: SelectOption[];
  workType?: string;
} & Pick<
  CreateJobType,
  'location' | 'department' | 'status' | 'visibility' | 'deadline' | 'dateScheduledPublish'
>;

const Form = createForm();

export const CreateJob = () => {
  const { jobId } = useParams();
  const jobQuery = useGetJob(Number(jobId), jobId !== undefined);
  if (jobQuery.isLoading && jobQuery.fetchStatus !== 'idle') {
    return <RelativeSpinner />;
  }
  if (jobQuery.isLoading && jobQuery.fetchStatus === 'idle') {
    return <RenderJobPosting />;
  }
  if (jobQuery.data === undefined) {
    return null;
  }
  return <RenderJobPosting job={jobQuery.data} />;
};

export const RenderJobPosting = ({ job }: { job?: Job }) => {
  const formStore = useCreateFormStore();
  const { t, lang } = useScopedTranslate(jobPostingLocale);
  const navigate = useNavigate();
  const { current } = useStore();
  const { showModal } = useModal();
  const { isEnterprise, isDeadlineRequired } = current.info;
  const { hasRolePermission } = usePermissions();
  const createJob = useCreateJob();
  const updateJob = useUpdateJob();
  const jobLabelQuery = useJobLabels();
  const workTypeQuery = useWorkTypes(lang);
  const {
    languages,
    languageStatus,
    noLangEnabled,
    isDirtyLanguages,
    missingTranslations,
    anyMissingTranslations,
    resetLanguages,
    LanguageBox,
    languageBoxProps,
  } = useJobLanguages(job);
  const [submitting, setSubmitting] = useState(false);

  const locations = [
    {
      label: (
        <Stack align="center" orientation="horizontal">
          <IconAddPlus />
          {t('addLocation')}
        </Stack>
      ),
      value: -1,
      tooltip: !hasRolePermission('locations:add') ? t('locationAddPermission') : undefined,
      isDisabled: !hasRolePermission('locations:add'),
    },
    ...current.info.locations
      .filter((location: ILocation) => {
        const currentJobLocationId = job?.location ? Number(job.location.id) : null;
        return (
          location.isActive ||
          (currentJobLocationId !== null && location.id === currentJobLocationId)
        );
      })
      .map((location: ILocation) => {
        const translations = location.translations;
        const translation =
          translations.find((translation: ITranslation) => translation.language === lang) ??
          translations.find(Boolean);
        const label = translation ? translation.title : t('missingLocationTranslation');
        return {
          label,
          value: location.id,
        };
      }),
  ];

  const departments = current.info.departments.map((department: IDepartment) => {
    const translation = department.translations.find(
      (translation) => translation.language === lang
    );
    const label = translation ? translation.title : t('departmentTranslationMissing');
    return {
      label: label,
      value: department.id,
    };
  });

  const jobLabels = [
    {
      label: (
        <Stack align="center" orientation="horizontal">
          <IconAddPlus />
          {t('addLabel')}
        </Stack>
      ),
      value: '-1',
      tooltip: !hasRolePermission('joblabels:add') ? t('jobLabelsAddPermission') : undefined,
      isDisabled: !hasRolePermission('joblabels:add'),
    },
    ...(jobLabelQuery.data ?? []).map((joblabel: IJobLabel) => ({
      label: joblabel.title,
      value: joblabel.id,
      color: joblabel.color,
    })),
  ] as SelectOption[];

  const workTypes = (workTypeQuery.data ?? [])
    .filter((workType) => workType.enabled || (job?.workType && workType.id === job?.workType?.id))
    .map((workType: WorkType & { title: string }) => ({
      value: workType.id.toString(),
      label: workType.title,
    })) as SelectOption[];

  const defaultLabels = job
    ? jobLabels.filter((a) => job.labels.some((label) => label.id === a.value))
    : [];

  const prepareData = (formData: JobPostingJobType) => {
    const languageData = languages.map((language) => ({
      language: language,
      description: formData[`${language}Description`],
      shortDescriptionHtml: formData[`${language}ShortDescription`] ?? '',
      title: formData[`${language}Title`],
      isEnabled: languageStatus.find((l) => l.code === language)?.isEnabled ?? true,
    }));
    return {
      ...formData,
      workType: parseInt(formData.workType ?? '', 10),
      languages: languageData,
      ...(formData.deadline ? { deadline: format(new Date(formData.deadline), 'yyyy-MM-dd') } : {}),
      ...(formData.dateScheduledPublish
        ? { dateScheduledPublish: format(new Date(formData.dateScheduledPublish), 'yyyy-MM-dd') }
        : {}),
      labels: (formData.labels ?? []).map((label) => label.value),
    };
  };

  const values = formStore.useFormValues();
  const isInvalidLocation = values['location'] === -1;
  const isDeadlineEnabled = values['deadlineSwitch'];
  const isScheduledPublishedDateEnabled = values['schedulePublishSwitch'];

  const onSaveAndContinue = async () => {
    setSubmitting(true);
    await submitForm();
    setSubmitting(false);
    const hasErrors = Object.keys(formStore.getState().errors).length > 0;
    if (hasErrors) {
      return;
    }
    if (job) {
      navigate(`/job/${job.id}/edit/form`, { state: 'override' });
    }
  };

  const submitForm = async () => {
    const formErrors: { [key: string]: string } = {};
    if (isInvalidLocation) {
      formErrors['location'] = 'required';
    }
    if (values['dateScheduledPublish'] && new Date() > values['dateScheduledPublish']) {
      formErrors['dateScheduledPublish'] = t('dateMustBeInFuture');
    }
    if (anyMissingTranslations) {
      languages.forEach((language) => {
        if (missingTranslations && missingTranslations[language]) {
          if (values[`${language}Title`] === '') {
            formErrors[`${language}Title`] = 'translation';
          }
          const description = values[`${language}Description`];
          if (typeof description === 'string' && isEditorHtmlEmpty(description)) {
            formErrors[`${language}Description`] = 'translation';
          }
          const shortDescription = values[`${language}ShortDescription`];
          if (typeof shortDescription === 'string' && isEditorHtmlEmpty(shortDescription)) {
            formErrors[`${language}ShortDescription`] = 'translation';
          }
        }
      });
    }
    languages.forEach((language) => {
      const htmlContentLength = getHtmlContentLength(values[`${language}ShortDescription`]);
      if (htmlContentLength > shortDescriptionMaxLength) {
        formErrors[`${language}ShortDescription`] = 'maxCharacters';
      }
    });
    formStore.actions.setErrors(formErrors);
    if (Object.keys(formErrors).length > 0) {
      return;
    }
    if (!formStore.actions.validateForm()) {
      return;
    }
    const data = prepareData(values);
    if (data) {
      if (!data.deadlineSwitch) {
        data.deadline = null;
      }
      delete data.deadlineSwitch;

      if (!data.schedulePublishSwitch) {
        data.dateScheduledPublish = null;
      }
      delete data.schedulePublishSwitch;

      if (!job) {
        await createJob.mutateAsync(data);
      } else {
        await updateJob.mutateAsync({ jobId: job.id, job: data });
      }
    }
  };

  const isDirtyForm = formStore.useIsDirty();
  const isDirty = isDirtyForm || isDirtyLanguages;

  useUnsavedChangesPrompt({
    isDirty: isDirty,
    onSave: async () => {
      await submitForm();
    },
    isPublished: job ? job.isPublished : false,
  });

  useEffect(() => {
    if (values['location'] === -1) {
      showModal('LOCATION_FORM', {
        onSuccess: (id: number) => {
          formStore.actions.setValue('location', id);
        },
      });
      if (values['location'] === -1) {
        formStore.actions.setValue('location', job?.location ? job.location.id : null);
      }
    }
  }, [values, showModal, formStore.actions, job?.location]);

  useEffect(() => {
    const labels = values['labels'] as SelectOption[];
    const newLabels = labels ? labels?.filter((label) => label.value !== '-1') : [];
    if (labels && newLabels.length !== labels.length) {
      formStore.actions.setValue('labels', newLabels);
      showModal('ADD_JOB_LABEL', {
        onSuccess: (label: string, value: number, color: string) => {
          formStore.actions.setValue('labels', [...newLabels, { label, value, color }]);
        },
      });
    }
  }, [values, showModal, formStore.actions]);

  const resetJobPosting = () => {
    formStore.actions.resetStore();
    resetLanguages();
  };

  const hasMultipleLanguages = languages.length > 1;
  const careerSiteMultipleLanguages = current.info.languages.length > 2;

  const isIcelandicOnlyJob =
    !careerSiteMultipleLanguages &&
    ((!job && current.info.defaultLanguage === 'is') || (job && languages.includes('is')));

  const isEnglishOnlyJob =
    !careerSiteMultipleLanguages &&
    ((!job && current.info.defaultLanguage === 'en') || (job && languages.includes('en')));

  return (
    <>
      <Header
        breadcrumbs={<Breadcrumb items={[{ label: job ? job.title : t('newJobPost') }]} />}
        actions={
          <Actions
            job={job}
            hasUnsavedChanges={isDirty}
            onSave={submitForm}
            onCancel={resetJobPosting}
            noLangEnabled={noLangEnabled}
            dataCyCancel="cancel-create-job"
            dataCySave="save-create-job"
            isSaving={createJob.isLoading || updateJob.isLoading}
          />
        }
      />
      <PageContent>
        <Grid>
          {current.langs.length > 1 && (
            <ColumnLeftSidebar>
              <LanguageBox {...languageBoxProps} />
            </ColumnLeftSidebar>
          )}
          <ColumnMiddleWithLeftSidebar>
            <Stack space={6}>
              {careerSiteMultipleLanguages || languages.length > 1 ? (
                <Text font="heading2" text={t('title')} />
              ) : (
                <Stack space={4} orientation="horizontal">
                  {isEnglishOnlyJob && <IconFlagGB tooltip={t('carrerSiteLanguageEN')} />}
                  {isIcelandicOnlyJob && <IconFlagIS tooltip={t('carrerSiteLanguageIS')} />}
                  <Text font="heading2" text={t('title')} />
                </Stack>
              )}
              <Separator />
              <Stack space={4}>
                <Text font="bold18" text={t('details')} />
                <Form store={formStore} onSubmit={() => submitForm()} space={8}>
                  <Stack space={4}>
                    {languages.map((language) => {
                      const languageTitle =
                        job?.languages.find((l) => l.language === language)?.title ?? '';
                      const title = `${language}Title`;
                      return (
                        <Form.TextField
                          key={title}
                          id={title}
                          labelIcon={hasMultipleLanguages ? FLAG_MAPPER_SMALL[language] : undefined}
                          label={
                            hasMultipleLanguages
                              ? t('jobTitle', { lang: language.toUpperCase() })
                              : t('jobTitleNoLang')
                          }
                          defaultValue={languageTitle}
                          validators={['required']}
                        />
                      );
                    })}
                    <StyledFormStack>
                      <Form.Select
                        id="location"
                        label={t('location')}
                        options={locations}
                        defaultValue={job?.location ? job.location.id : ''}
                        validators={['required']}
                        isClearable={false}
                      />
                      {isEnterprise && (
                        <Form.Select
                          id="department"
                          label={t('department')}
                          options={departments}
                          defaultValue={job?.department ?? ''}
                          isClearable={false}
                        />
                      )}
                    </StyledFormStack>
                    <StyledFormStack>
                      <Form.Select
                        id="workType"
                        label={t('worktype')}
                        options={workTypes}
                        defaultValue={(job?.workType?.id ?? '').toString()}
                        isClearable={true}
                      />

                      <Form.Select
                        id="status"
                        label={t('jobStatus')}
                        options={[
                          { value: 'full_time', label: t('fullTime') },
                          { value: 'part_time', label: t('partTime') },
                          { value: 'per_diem', label: t('perDiem') },
                          { value: 'not_specified', label: t('notSpecified') },
                        ]}
                        defaultValue={job ? job.status : 'full_time'}
                        isClearable={false}
                      />
                    </StyledFormStack>
                    <Form.Select
                      id="visibility"
                      label={t('visibility')}
                      options={[
                        { value: 'public', label: t('visabilityPublic') },
                        { value: 'private', label: t('visabilityPrivate') },
                      ]}
                      defaultValue={job ? job.visibility : 'public'}
                      isClearable={false}
                    />
                    <Form.SelectMulti
                      id="labels"
                      label={t('jobLabels')}
                      labelInfo={t('jobLabelsInfo')}
                      options={jobLabels}
                      defaultValue={defaultLabels}
                      key={`labels-${jobLabelQuery.isLoading}`}
                    />
                    <Card variant="transparent" space={4}>
                      <Stack space={4}>
                        <Stack
                          space={4}
                          orientation="horizontal"
                          align="center"
                          justify="space-between"
                        >
                          <Stack>
                            <FormLabel
                              text={t('scheduledPublishedDate')}
                              info={t('scheduledPublishedDateInfo')}
                            />
                            {isScheduledPublishedDateEnabled ? (
                              <Text text={t('enabled')} />
                            ) : (
                              <Text
                                color="purple50"
                                text={
                                  job?.isPublished
                                    ? t('jobAlreadyPublished')
                                    : job?.isArchived
                                    ? t('jobIsArchived')
                                    : t('disabledScheduledPublish')
                                }
                              />
                            )}
                          </Stack>
                          <Form.Switch
                            dataCy="schedulePublishSwitch"
                            id="schedulePublishSwitch"
                            label={t('scheduledPublishedDateInfo')}
                            labelVisible={false}
                            isDisabled={job?.isArchived || job?.isPublished}
                            key={`schedulePublishSwitch-${job?.dateScheduledPublish}`}
                            defaultValue={job ? job?.dateScheduledPublish !== null : false}
                          />
                        </Stack>
                        <Collapse isCollapsed={!isScheduledPublishedDateEnabled}>
                          <Form.DatePicker
                            id="dateScheduledPublish"
                            label={t('publishedOn')}
                            placeholder="DD / MM / YYYY"
                            validators={['required']}
                            defaultValue={job ? job.dateScheduledPublish : undefined}
                          />
                        </Collapse>
                      </Stack>
                    </Card>
                    <Card variant="transparent" space={4}>
                      <Stack space={4}>
                        <Stack
                          space={4}
                          orientation="horizontal"
                          align="center"
                          justify="space-between"
                        >
                          <Stack>
                            <FormLabel
                              text={
                                isDeadlineRequired
                                  ? t('applicationDeadlineRequired')
                                  : t('applicationDeadline')
                              }
                              info={t('applicationDeadlineInfo')}
                            />
                            {isDeadlineEnabled ? (
                              <Text text={t('enabled')} />
                            ) : (
                              <Text color="purple50" text={t('disabled')} />
                            )}
                          </Stack>
                          <Form.Switch
                            dataCy="deadlineSwitch"
                            id="deadlineSwitch"
                            label={t('applicationDeadlineInfo')}
                            labelVisible={false}
                            isDisabled={isDeadlineRequired}
                            defaultValue={
                              isDeadlineRequired || (job ? job?.deadline !== null : false)
                            }
                          />
                        </Stack>
                        <Collapse isCollapsed={!isDeadlineEnabled}>
                          <Form.DatePicker
                            id="deadline"
                            label={t('deadline')}
                            placeholder="DD / MM / YYYY"
                            validators={['required']}
                            defaultValue={job ? job.deadline : undefined}
                          />
                        </Collapse>
                      </Stack>
                    </Card>
                  </Stack>
                  <Separator />
                  <Stack space={2}>
                    <Text font="bold18" text={t('description')} />
                    <Text font="regular14" text={t('descriptionHelpText')} />
                  </Stack>
                  <Stack space={4}>
                    {languages.map((language) => {
                      const description = job
                        ? job.languages.find((l: JobLanguage) => l.language === language)
                            ?.shortDescriptionHtml
                        : '';
                      const title = `${language}ShortDescription`;
                      return (
                        <ShortDescriptionHtml
                          key={title}
                          id={title}
                          labelIcon={hasMultipleLanguages ? FLAG_MAPPER_SMALL[language] : undefined}
                          label={
                            hasMultipleLanguages
                              ? t('shortDescription', { lang: language.toUpperCase() })
                              : t('shortDescriptionNoLang')
                          }
                          description={description}
                          placeholder={t('shortDescriptionPlaceholder')}
                        />
                      );
                    })}
                    {languages.map((language) => {
                      const description = job
                        ? job.languages.find((l: JobLanguage) => l.language === language)
                            ?.description
                        : '';
                      const title = `${language}Description`;
                      return (
                        <JobDescriptionEditor
                          key={title}
                          id={title}
                          labelIcon={hasMultipleLanguages ? FLAG_MAPPER_SMALL[language] : undefined}
                          label={
                            hasMultipleLanguages
                              ? t('longDescription', { lang: language.toUpperCase() })
                              : t('longDescriptionNoLang')
                          }
                          description={description}
                          placeholder={t('longDescriptionPlaceholder')}
                        />
                      );
                    })}
                  </Stack>
                </Form>
              </Stack>
              <Separator />
              {isDirty || !job ? (
                <Stack orientation="horizontal" justify="flex-end">
                  <Button
                    text={job?.isPublished ? t('publishAndContinue') : t('saveAndContinue')}
                    variant="primary-blue"
                    onClick={async () => {
                      await onSaveAndContinue();
                    }}
                    dataCy="save-and-continue-create-job"
                    disabled={submitting}
                  />
                </Stack>
              ) : (
                <Stack orientation="horizontal" justify="flex-end">
                  <Link to={`/job/${job.id}/edit/form`}>
                    <Button text={t('continue')} variant="primary-blue" />
                  </Link>
                </Stack>
              )}
            </Stack>
          </ColumnMiddleWithLeftSidebar>
        </Grid>
      </PageContent>
    </>
  );
};

const StyledFormStack = styled(Stack).attrs({
  space: 3,
  justify: 'space-between',
  orientation: 'horizontal',
})({ '>*': { width: '100%' } });

const jobPostingLocale = {
  en: {
    newJobPost: 'New job post',
    languages: 'Languages',
    title: 'Job Posting',
    details: 'Details',
    jobTitle: 'Job title • {lang}',
    jobTitleNoLang: 'Job title',
    location: 'Location',
    department: 'Department',
    worktype: 'Work type',
    jobStatus: 'Job status',
    visibility: 'Visibility',
    deadline: 'Select deadline date',
    applicationDeadline: 'Application deadline (optional)',
    applicationDeadlineRequired: 'Application deadline (required)',
    applicationDeadlineInfo:
      'The date when the application deadline expires automatically. It expires on midnight on the selected date',
    disabled: 'Disabled - no deadline date set',
    enabled: 'Enabled',
    description: 'Job description',
    descriptionHelpText:
      'A great job description is an essential step towards attracting the right candidates.',
    shortDescription: 'Short description • {lang}',
    shortDescriptionNoLang: 'Short description',
    shortDescriptionPlaceholder: 'Describe the position in short detail',
    longDescription: 'Long description • {lang}',
    longDescriptionNoLang: 'Long description',
    longDescriptionPlaceholder: 'Add a detailed job description',
    draft: 'Draft',
    unsavedChanges: 'Unsaved changes',
    cancel: 'Cancel',
    save: 'Save',
    publish: 'Publish',
    published: 'Published',
    noNewChanges: 'No new changes',
    missingLocationTranslation: 'Missing location translation',
    addLocation: 'Add location',
    locationAddPermission: 'Contact a super-admin to add more locations',
    departmentTranslationMissing: 'Missing translation for department',
    fullTime: 'Full time',
    partTime: 'Part time',
    perDiem: 'Per diem',
    notSpecified: 'Not specified',
    visabilityPublic: 'Shown on career page (public)',
    visabilityPrivate: 'Only via direct link (private)',
    saveAndContinue: 'Save & Continue',
    translationMissing: 'Translation missing',
    continue: 'Continue',
    nolanguageEnabled: 'No language enabled',
    publishAndContinue: 'Publish changes & Continue',
    carrerSiteLanguageIS: 'Your career site is set to Icelandic',
    carrerSiteLanguageEN: 'Your career site is set to English',
    jobLabels: 'Job labels',
    jobLabelsAddPermission: 'Contact a super-admin to add more job labels',
    addLabel: 'Add label',
    jobLabelsInfo: 'Labels can be used to better organize and group your job postings',
    scheduledPublishedDate: 'Scheduled publish date',
    scheduledPublishedDateInfo: 'Use for scheduling the publish date',
    publishedOn: 'Published on',
    dateMustBeInFuture: 'Scheduled publish date must be in the future',
    disabledScheduledPublish: 'Disabled - no scheduled publish date set',
    jobAlreadyPublished: 'Job already published',
    jobIsArchived: 'Job is archived',
  },
  is: {
    newJobPost: 'Ný starfsauglýsing',
    languages: 'Tungumál',
    title: 'Starfsauglýsing',
    details: 'Upplýsingar',
    jobTitle: 'Starfstitill • {lang}',
    jobTitleNoLang: 'Starfstitill',
    location: 'Staðsetning',
    department: 'Deild',
    worktype: 'Tegund starfs',
    jobStatus: 'Starfshlutfall',
    visibility: 'Sýnileiki starfs',
    deadline: 'Veldu dagsetningu',
    applicationDeadline: 'Umsóknarfrestur (valkvætt)',
    applicationDeadlineRequired: 'Umsóknarfrestur (krafist)',
    applicationDeadlineInfo:
      'Dagsetning sem umsóknarfrestur rennur út sjálfvirkt. Frestur rennur út á miðnætti á valinni dagsetningu',
    disabled: 'Óvirkt - engin dagsetning valin',
    enabled: 'Virkt',
    description: 'Starfslýsing',
    descriptionHelpText: 'Góð starfslýsing er mikilvægt skref til að vekja áhuga umsækjenda.',
    shortDescription: 'Stutt starfslýsing • {lang}',
    shortDescriptionNoLang: 'Stutt starfslýsing',
    shortDescriptionPlaceholder: 'Stutt lýsing á starfi',
    longDescription: 'Löng starfslýsing • {lang}',
    longDescriptionNoLang: 'Löng starfslýsing',
    longDescriptionPlaceholder: 'Bæta við nánari lýsingu á starfi',
    draft: 'Drög',
    unsavedChanges: 'Óvistaðar breytingar',
    cancel: 'Hætta við',
    save: 'Vista',
    publish: 'Birta',
    published: 'Í loftinu',
    noNewChanges: 'Engar nýjar breytingar',
    missingLocationTranslation: 'Vantar staðsetningar þýðingu',
    addLocation: 'Bæta við staðsetningu',
    locationAddPermission: 'Hafðu samband við ofurnotanda til að bæta við staðsetningu',
    departmentTranslationMissing: 'Vantar þýðingu á deild',
    fullTime: 'Full vinna',
    partTime: 'Hlutastarf',
    perDiem: 'Dagpeningur',
    notSpecified: 'Á ekki við',
    visabilityPublic: 'Sýna á ráðningarsíðu',
    visabilityPrivate:
      'Ekki sýna starf á ráðningasíðu. Bara hægt að sækja um starf í gegnum beinan hlekk.',
    saveAndContinue: 'Vista & Halda áfram',
    translationMissing: 'Þýðingu vantar',
    continue: 'Halda áfram',
    nolanguageEnabled: 'Engin tungumál virk',
    publishAndContinue: 'Birta breytingar & Halda áfram',
    carrerSiteLanguageIS: 'Ráðningarsíðan er stillt á Íslensku',
    carrerSiteLanguageEN: 'Ráðningarsíðan er stillt á Ensku',
    jobLabels: 'Merki starfs',
    jobLabelsAddPermission: 'Hafðu samband við ofurnotanda til að bæta við merki starfs',
    addLabel: 'Bæta við merki',
    jobLabelsInfo: 'Merki geta verið notuð til að skipuleggja og flokka störf',
    scheduledPublishedDate: 'Dagsetning birtingar',
    scheduledPublishedDateInfo: 'Til að stilla útgáfudag',
    publishedOn: 'Birta þann',
    dateMustBeInFuture: 'Dagsetning birtingar verður að vera í framtíð',
    disabledScheduledPublish: 'Óvirkt - engin dagsetning birtingar valin',
    jobAlreadyPublished: 'Starf þegar birt',
    jobIsArchived: 'Starf er í geymslu',
  },
};
